// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AccountModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modal from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BillingInfoForm from "@steiner/App/Components/CustomCreateAccount/BillingInfoForm";
import ContactInformation from "@steiner/App/Components/CustomCreateAccount/ContactInfoForm";
import LoginInformationForm from "@steiner/App/Components/CustomCreateAccount/LoginInfoForm";
import ShippingInfoForm from "@steiner/App/Components/CustomCreateAccount/ShippingInfoForm";
import { StepperComponent, StepperStyles } from "@steiner/App/Widgets/Stepper/Stepper";
import { StepperContext } from "@steiner/App/Widgets/Stepper/StepperContext";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { ContactInformationFields, LoginInformationFields, XAddressModel } from "@steiner/XDL/types";
import React, { RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    addAccount,
    signIn,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateCustomerNewAccountStyles {
    stepperStyles?: StepperStyles;
    stepWrapper?: InjectableCss;
}

export const createCustomerNewAccountStyles: CreateCustomerNewAccountStyles = {};

const styles = createCustomerNewAccountStyles;

export const initialAddress: XAddressModel = {
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    attention: "",
    city: "",
    companyName: "",
    contactFullName: "",
    country: {
        abbreviation: "",
        id: "",
        name: "",
        properties: {},
        states: [],
        uri: "",
    },
    customerName: "",
    customerNumber: "",
    customerSequence: "",
    email: "",
    fax: "",
    firstName: "",
    fullAddress: "",
    id: "",
    lastName: "",
    phone: "",
    postalCode: "",
    properties: {},
    state: {
        abbreviation: "",
        id: "",
        name: "",
        properties: {},
        uri: "",
    },
    uri: "",
    validation: {
        companyName: { maxLength: 30, isDisabled: false, isRequired: true },
        address1: { maxLength: 30, isDisabled: false, isRequired: true },
        address2: { maxLength: 30, isDisabled: false, isRequired: false },
        address3: { maxLength: 30, isDisabled: false, isRequired: false },
        address4: { maxLength: 30, isDisabled: false, isRequired: false },
        country: { maxLength: 30, isDisabled: false, isRequired: false },
        state: { maxLength: 30, isDisabled: false, isRequired: true },
        city: { maxLength: 20, isDisabled: false, isRequired: true },
        postalCode: { maxLength: 10, isDisabled: false, isRequired: true },
        attention: { maxLength: 30, isDisabled: false, isRequired: false },
        email: { maxLength: 30, isDisabled: false, isRequired: false },
        fax: { maxLength: 14, isDisabled: false, isRequired: false },
        firstName: { maxLength: 30, isDisabled: false, isRequired: false },
        lastName: { maxLength: 30, isDisabled: false, isRequired: false },
        phone: { maxLength: 14, isDisabled: false, isRequired: false },
    },
};

const CreateCustomerNewAccount = ({ id, validateReCaptcha, addAccount, signIn }: Props) => {
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const { showStep, setCurrentStep, currentStep } = useContext(StepperContext);
    const [loginInformation, setLoginInformation] = useState<LoginInformationFields>();
    const [contactInformation, setContactInformation] = useState<ContactInformationFields>();
    const [billingInformation, setBillingInformation] = useState<XAddressModel>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState("");
    const { returnUrl } = useAppSelector(state => {
        return {
            returnUrl: getReturnUrl(state),
        };
    });

    useEffect(() => {
        if (error && currentStep !== 4) {
            setError("");
        }
    }, [currentStep, error]);

    const handleSubmitLoginInformation = (loginInformation: LoginInformationFields) => {
        setLoginInformation(loginInformation);
        setCurrentStep(2);
    };

    const handleSubmitContactInformation = (contactInformation: ContactInformationFields) => {
        setContactInformation(contactInformation);
        setCurrentStep(3);
    };

    const handleSubmitBillingInfoForm = (billingInfo: XAddressModel) => {
        setBillingInformation(billingInfo);
        setCurrentStep(4);
    };

    const handleSubmitAddressesInfo = async (
        billingAddress: XAddressModel,
        shippingAddress: XAddressModel,
        useBillingAddress: boolean,
    ) => {
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid || !loginInformation) {
            return;
        }
        addAccount({
            firstName: contactInformation?.firstName,
            lastName: contactInformation?.lastName,
            email: loginInformation.emailAddress,
            password: loginInformation.password,
            properties: {
                company: contactInformation?.companyName,
                jobTitle: contactInformation?.jobTitle,
                phone: contactInformation?.phoneNumber,
                btAddress1: billingAddress.address1,
                btAddress2: billingAddress.address2,
                btCity: billingAddress.city,
                btState: billingAddress?.state?.abbreviation,
                btCountry: billingAddress?.country?.abbreviation,
                btPostalCode: billingAddress.postalCode,
                stFirstName: shippingAddress.firstName,
                stLastName: shippingAddress.lastName,
                stAddress1: shippingAddress.address1,
                stAddress2: shippingAddress.address2,
                stCity: shippingAddress.city,
                stState: shippingAddress.state?.abbreviation,
                stCountry: shippingAddress.country?.abbreviation,
                stPostalCode: shippingAddress.postalCode,
                shipToAsBillTo: useBillingAddress,
            },
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                setError(error);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as any);
    };

    const onSuccessHandler = (account?: AccountModel) => {
        setError("");
        setIsModalOpen(true);
    };

    const handleSingIn = () => {
        if (!loginInformation) {
            return;
        }
        setTimeout(() => {
            signIn({
                userName: loginInformation.emailAddress,
                password: loginInformation.password,
                rememberMe: false,
                returnUrl,
            });
        }, 500);
    };

    return (
        <>
            <StepperComponent extendedStyles={styles.stepperStyles} allowMovingForward={true} />
            <StyledWrapper {...styles.stepWrapper} {...{ showStep: showStep(1) }}>
                <LoginInformationForm onSubmit={handleSubmitLoginInformation} />
            </StyledWrapper>
            <StyledWrapper {...styles.stepWrapper} {...{ showStep: showStep(2) }}>
                <ContactInformation onSubmit={handleSubmitContactInformation} onBackClick={() => setCurrentStep(1)} />
            </StyledWrapper>
            <StyledWrapper {...styles.stepWrapper} {...{ showStep: showStep(3) }}>
                <BillingInfoForm onSubmit={handleSubmitBillingInfoForm} onBackClick={() => setCurrentStep(2)} />
            </StyledWrapper>
            <StyledWrapper {...styles.stepWrapper} {...{ showStep: showStep(4) }}>
                <ShippingInfoForm
                    recaptchaRef={recaptchaRef}
                    onSubmit={handleSubmitAddressesInfo}
                    onBackClick={() => setCurrentStep(3)}
                    error={error}
                    billingAddress={
                        {
                            ...billingInformation,
                            firstName: contactInformation?.firstName,
                            lastName: contactInformation?.lastName,
                            email: loginInformation?.emailAddress,
                        } as XAddressModel
                    }
                />
            </StyledWrapper>
            <Modal
                headline={translate("Account Creation Successful")}
                isOpen={isModalOpen}
                handleClose={() => {
                    setIsModalOpen(false);
                    handleSingIn();
                }}
            >
                <Typography>{siteMessage("Steiner_Request_New_User_Success_Message")}</Typography>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CreateCustomerNewAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
